import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Inbox, FileText, Plus, LogOut, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "./api";
import { useAdminAuth } from "./AdminApp";

const useSast = () => {
  const [t, setT] = useState("");
  useEffect(() => {
    const tick = () =>
      setT(
        new Intl.DateTimeFormat("en-GB", {
          timeZone: "Africa/Johannesburg",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).format(new Date())
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return t;
};

const tabClass = ({ isActive }) =>
  `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition ${
    isActive ? "bg-white text-black" : "text-neutral-400 hover:bg-white/10 hover:text-white"
  }`;

export default function AdminLayout({ children }) {
  const time = useSast();
  const navigate = useNavigate();
  const { setAuth } = useAdminAuth();

  const logout = async () => {
    try {
      await api.post("/logout");
    } catch {}
    setAuth(false);
    toast.success("Signed out");
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0a0a0c]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-4">
            <span className="font-heading text-sm font-semibold tracking-tight">
              AFR · Command Center
            </span>
            <span className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-neutral-300 sm:inline-flex">
              <Clock size={13} /> <span className="tabular-nums">{time}</span> SAST
            </span>
          </div>
          <nav className="order-3 flex w-full items-center gap-2 sm:order-2 sm:w-auto">
            <NavLink to="/admin/inquiries" className={tabClass} data-testid="tab-inquiries">
              <Inbox size={15} /> Inquiries
            </NavLink>
            <NavLink to="/admin/invoices" className={tabClass} data-testid="tab-invoices">
              <FileText size={15} /> Quotes & Invoices
            </NavLink>
          </nav>
          <div className="order-2 flex items-center gap-2 sm:order-3">
            <button
              data-testid="new-doc-btn"
              onClick={() => navigate("/admin/invoices?new=1")}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-neutral-200"
            >
              <Plus size={15} /> New
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-neutral-300 transition hover:bg-white/10 hover:text-white"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">{children}</main>
    </div>
  );
}
